"use client"
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Send } from "lucide-react";

export default function FollowUpQuestion({ response }) {
    const [followUpQuestion, setFollowUpQuestion] = useState("");
    const navigate = useNavigate();

    const handleAskQuestion = () => {
        if (!followUpQuestion.trim()) return;

        // Save the full backend response in localStorage
        localStorage.setItem("clinical_decision_response", JSON.stringify(response));

        // Build a context summary from the report
        const report = response.physician_report;
        const caseSummary = report?.report_metadata?.case_summary || '';
        const demographics = report?.patient_overview?.demographics || '';
        const chiefComplaint = report?.patient_overview?.chief_complaint || '';
        const triageLevel = report?.patient_overview?.triage_level || '';
        const diagnoses = report?.differential_diagnosis?.map(d => d.diagnosis).join(', ') || '';
        const vitalSigns = report?.clinical_analysis?.vital_signs_interpretation || '';
        const management = report?.management_plan?.pharmacological?.map(m => `${m.drug} ${m.dose}`).join(', ') || '';

        const contextMessage = [
            `[Clinical Decision Report Context]`,
            caseSummary && `Case Summary: ${caseSummary}`,
            demographics && `Patient: ${demographics}`,
            chiefComplaint && `Chief Complaint: ${chiefComplaint}`,
            triageLevel && `Triage Level: ${triageLevel}`,
            diagnoses && `Differential Diagnosis: ${diagnoses}`,
            vitalSigns && `Vitals: ${vitalSigns}`,
            management && `Current Medications: ${management}`,
            ``,
            `[My Question]`,
            followUpQuestion.trim(),
        ].filter(Boolean).join('\n');

        // Save the constructed query
        localStorage.setItem("clinical_decision_query", contextMessage);

        // Navigate to chat page
        navigate("/chatmed");
        localStorage.setItem("selectedKey", "chatmed");
    };

    return (
        <div className="mt-6 sm:mt-8 p-3 sm:p-4 md:p-5 lg:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0B7BC1] flex-shrink-0" />
                Do you have any questions?
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                Ask a follow-up question about this clinical report and get further insights from ChatMed.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <input
                    type="text"
                    value={followUpQuestion}
                    onChange={(e) => setFollowUpQuestion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAskQuestion(); }}
                    placeholder="Type your question here..."
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B7BC1] focus:border-transparent bg-white"
                />
                <button
                    onClick={handleAskQuestion}
                    disabled={!followUpQuestion.trim()}
                    className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm font-semibold text-white transition-all duration-200 ${
                        followUpQuestion.trim()
                            ? 'bg-[#0B7BC1] hover:bg-[#096aa8] cursor-pointer shadow-md hover:shadow-lg'
                            : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                    <Send className="w-4 h-4" />
                    <span className="hidden sm:inline">Ask ChatMed</span>
                    <span className="sm:hidden">Ask</span>
                </button>
            </div>
        </div>
    );
}
